import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Link as MuiLink } from '@mui/material';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';

import BreadcrumbTrail from '../../../../layouts/BreadcrumbTrail';
import CenteredLoader from '../../../../components/loaders/PageLoader';
import DateRangePicker from '../../../../components/filters/DateRangePicker';

import UnitDetailsTree from '../../components/UnitDetails/UnitDetailsTree';
import UnitDetailsSummaryCard from '../../components/UnitDetails/UnitDetailsSummaryCard';
import UnitEnergyConsumptionChart, {
  UnitDetailsConsumptionLegend,
} from '../../components/UnitDetails/UnitDetailsConsumptionChart';

import { makeDefaultDateRange21 } from '../../utils/dates';
import { useUnitDetails } from '../../hooks/useUnitDetails';
import styles from './UnitDetails.styles';

import { useCrumb, useNavWithTrail } from '../../../../layouts/TrailProvider';

export default function UnitDetails() {
  const { isSidebarExpanded } = useOutletContext();
  const { unitName } = useParams();
  const navigate = useNavigate();
  const nav = useNavWithTrail(navigate); 

  useCrumb(`Unit ${unitName}`, `/home/units/${encodeURIComponent(unitName)}`);

  const { loading, unitData, evnData, dash, summaryItems } = useUnitDetails(unitName);
  const [unitConsumptionRange, setUnitConsumptionRange] = useState(makeDefaultDateRange21());

  const titleName = unitData?.name ?? unitName;

  return (
    <Box
      sx={{
        ...styles.unitDetails,
        marginLeft: isSidebarExpanded ? '240px' : '95px',
        width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
        transition: 'all 0.3s ease',
      }}
    >
      {loading ? (
        <CenteredLoader />
      ) : (
        <>
          <BreadcrumbTrail />

          <Box sx={styles.headerRow}>
            <Typography sx={styles.pageTitle}>{dash(titleName)}</Typography>
          </Box>

          <Box sx={styles.versionHistoryContainer}>
            <Typography variant="body2" sx={styles.versionInfoText}>
              Version history{' '}
              <MuiLink
                underline="none"
                sx={styles.versionLink}
                onClick={() =>
                  titleName &&
                  nav(`/home/units/${encodeURIComponent(titleName)}/history`)
                }
              >
                click here
              </MuiLink>
            </Typography>
          </Box>

          <Box sx={styles.grid}>
            <UnitDetailsSummaryCard summaryItems={summaryItems} dash={dash} />

            <Card sx={styles.detailCardSmall}>
              <Box sx={styles.cardHeaderRowLeft}>
                <Typography sx={styles.relatedMetersHeading}>Related EVNs & Meters</Typography>
              </Box>
              <CardContent sx={styles.cardContent}>
                {Array.isArray(evnData) && evnData.length > 0 ? (
                  <UnitDetailsTree evnData={evnData} unitName={titleName} />
                ) : (
                  <Typography sx={styles.emptyStateText}>No related EVNs —</Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={styles.detailCardMedium}>
              <Box sx={styles.cardHeaderRow}>
                <Typography sx={styles.relatedMetersHeading}>Energy Consumption by Unit</Typography>
                <Box sx={styles.headerControls}>
                  <DateRangePicker
                    dateRange={unitConsumptionRange}
                    setDateRange={setUnitConsumptionRange}
                  />
                  <UnitDetailsConsumptionLegend />
                </Box>
              </Box>

              <CardContent sx={styles.cardContent}>
                <UnitEnergyConsumptionChart unitName={titleName} dateRange={unitConsumptionRange} />
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
}
